import { gql, useMutation } from "@apollo/client";
import { setSignupOrigin } from "analytics/signupOrigin";
import navigateToApp from "components/website/components/navigateToApp";
import { usePlanGate } from "components/website/components/planGate";
import { openTrip } from "components/website/partners/madmonkey/openTrip";
import { useCallback, useEffect, useRef, useState } from "react";
import { clampNights, MAX_STOPS, type Stop } from "./plannerLogic";

/**
 * "Save this route": turn the route the visitor picked, or built, into a trip in their own Stippl account.
 *
 * A signed-out visitor first gets the site's login / register modal (the plan gate, in modal mode on a phone too: this CTA
 * overrides the hub's phones-to-store rule), and the save carries on by itself once they are in. A signed-in visitor goes straight
 * through. Afterwards a desktop opens the trip; a phone stays on the hub with a "saved" state, because the app is where the trip is
 * edited. Modelled on the Mad Monkey page's useGetThisTrip.
 *
 * `startFresh` is the panel's "Plan {country} in Stippl" / "Start from scratch" CTA: nothing to save, so a signed-in visitor goes to the app's
 * home, and a signed-out one gets the same signup modal on a gate of its own (`scratchGate`, with the modal's default `onLogin`: the app's home).
 *
 * The two mutations sit here, and not in graphql/mutations/trip.ts, so the marketing bundle does not pull the whole trip fragment in.
 */

export const COPY_TRIP = gql`
	mutation CopyTrip($tripId: ID!, $input: CopyTripInput!) {
		copyTrip(tripId: $tripId, input: $input) {
			_id
			name
		}
	}
`;

export const CREATE_TRIP_FROM_ROUTE = gql`
	mutation CreateTripFromRoute($input: CreateTripFromRouteInput!) {
		createTripFromRoute(input: $input) {
			_id
			name
		}
	}
`;

/** the attribution tag: the backend reports it on its partner_trip_created event and does not store it on the trip */
export const PARTNER = "countrymap";

/** What CopyTripModal copies when nothing is unticked (its CHECKBOX_KEYS): the whole trip. */
export const COPY_SELECTED = ["accommodations", "activities", "transport", "notes", "expenses"];

export type SaveJob =
	/** a ready-made route, copied from its trip; `from: "mine"` when My route is that route, unchanged, so the signup is My route's CTA */
	| { kind: "route"; routeId: string; name: string; nights: number; startDate: string | null; from?: "mine" }
	/** a route built or customised on the map, created from its stops */
	| { kind: "custom"; stops: Stop[]; name: string; startDate: string | null };

export type SaveStatus = "idle" | "working" | "saved" | "error";

/** What `saved` remembers a job by: a ready-made route is its id, a built one is its stops, so an edit after the save is a route that is not saved yet. */
export const jobKey = (job: SaveJob): string =>
	job.kind === "route" ? job.routeId : `custom:${job.startDate ?? ""}:${job.stops.map((s) => `${s.key}:${s.nights}`).join(",")}`;

/** A `YYYY-MM-DD` day as the date picker gives it to the backend: local noon, so no timezone moves it to the day before. */
const noon = (day: string): Date => new Date(`${day}T12:00:00`);

/** The dates of a copy: the start the visitor picked, else a month out (defaultCopyDates); as long as the route. */
async function copyDates(startDate: string | null, nights: number): Promise<{ startDate: string; endDate: string }> {
	if (startDate) {
		const start = noon(startDate);
		const end = new Date(start);
		end.setDate(end.getDate() + nights);
		return { startDate: start.toISOString(), endDate: end.toISOString() };
	}
	// Imported here, on a click: it brings moment, which the marketing bundle does not otherwise carry.
	const { defaultCopyDates } = await import("utils/copyTripDates");
	const dates = defaultCopyDates({ destinations: [{ duration: nights }] })!;
	return { startDate: dates.startDate.toISOString(), endDate: dates.endDate.toISOString() };
}

/** CopyTripModal strips these from the name before it copies */
const cleanName = (name: string): string => name.replace(/[;:'"%^&*.,]/g, "") || name;

/** The route as `createTripFromRoute` takes it (routeTrip.ts: RouteStopInput, CreateTripFromRouteInput). */
export function buildCreateTripInput(stops: Stop[], name: string, startDate: string | null) {
	return {
		name,
		partner: PARTNER,
		...(startDate ? { startDate: noon(startDate).toISOString() } : {}),
		stops: stops.slice(0, MAX_STOPS).map((s) => ({ city: s.name, country: s.country, latitude: s.lat, longitude: s.lng, nights: clampNights(s.nights) })),
	};
}

/**
 * Whether a Firebase session exists. Firebase is imported here, on demand, and not at the top of the file:
 * the server renders this page too, and server output never imports it. `onAuthStateChanged` answers once the
 * stored session has been read, so a reload mid-visit is not "signed out".
 */
async function hasSession(): Promise<boolean> {
	const { auth } = await import("components/firebase");
	return new Promise((resolve) => {
		const off = auth.onAuthStateChanged((user) => {
			off();
			resolve(!!user);
		});
	});
}

export function useSaveRoute() {
	const routeGate = usePlanGate("countrymap_route", "modal");
	const mineGate = usePlanGate("countrymap_myroute", "modal");
	const scratchGate = usePlanGate("countrymap_scratch", "modal");
	const [copyTrip] = useMutation(COPY_TRIP);
	const [createTrip] = useMutation(CREATE_TRIP_FROM_ROUTE);
	const [status, setStatus] = useState<SaveStatus>("idle");
	// The routes saved from this page on a phone: `status` follows the open card, so a saved route needs its own memory or it can be saved twice.
	const [saved, setSaved] = useState<ReadonlySet<string>>(() => new Set());
	const [signedIn, setSignedIn] = useState(false);
	const [gateName, setGateName] = useState<"route" | "mine">("route");
	const busy = useRef(false);
	const pending = useRef<SaveJob | null>(null);
	// Same on both gates; set in an effect, so the server render and the first client render agree on false.
	const { isPhone } = routeGate;

	// Callers hold `busy`; this lets it go again when the save does not leave the page.
	const run = useCallback(
		async (job: SaveJob) => {
			setStatus("working");
			try {
				let trip: { _id: string; name: string } | undefined;
				if (job.kind === "route") {
					const { startDate, endDate } = await copyDates(job.startDate, clampNights(job.nights));
					const input = { startDate, endDate, name: cleanName(job.name), selected: COPY_SELECTED };
					trip = (await copyTrip({ variables: { tripId: job.routeId, input } })).data?.copyTrip;
				} else {
					trip = (await createTrip({ variables: { input: buildCreateTripInput(job.stops, job.name, job.startDate) } })).data?.createTripFromRoute;
				}
				if (!trip?._id) throw new Error("no trip returned");
				if (isPhone) {
					busy.current = false;
					setSaved((ids) => new Set(ids).add(jobKey(job)));
					setStatus("saved");
					return;
				}
				// Leaves the page: the button stays busy until it does.
				openTrip(trip);
			} catch (e: any) {
				console.error("[CountryMap] saving the route failed:", { kind: job.kind, error: e?.message || e });
				busy.current = false;
				setStatus("error");
			}
		},
		[copyTrip, createTrip, isPhone]
	);

	const request = useCallback(
		async (job: SaveJob) => {
			if (busy.current || !(job.kind === "route" || job.stops.length)) return;
			busy.current = true;
			setStatus("working");
			let signedIn: boolean;
			try {
				signedIn = await hasSession();
			} catch (e: any) {
				// Firebase's chunk failed to load (an old chunk after a deploy, or offline): show the error line, not a button stuck on "Saving…".
				console.error("[CountryMap] the session check failed:", e?.message || e);
				busy.current = false;
				setStatus("error");
				return;
			}
			if (signedIn) {
				await run(job);
				return;
			}
			busy.current = false;
			setStatus("idle");
			pending.current = job;
			const gate = job.kind === "custom" || job.from === "mine" ? "mine" : "route";
			setGateName(gate);
			// So the signup this opens can say it started on this page (analytics/signupOrigin).
			setSignupOrigin("countrymap", job.kind === "route" ? job.routeId : "custom");
			(gate === "mine" ? mineGate : routeGate).requestPlan();
		},
		[run, routeGate, mineGate]
	);

	// After the modal signs the visitor in, the save continues by itself. LoginModal reports the sign-in while it renders,
	// so the work waits for the next effect instead of starting there.
	useEffect(() => {
		if (!signedIn) return;
		setSignedIn(false);
		const job = pending.current;
		pending.current = null;
		if (!job || busy.current) return;
		busy.current = true;
		void run(job);
	}, [signedIn, run]);

	const startFresh = useCallback(async () => {
		let signedIn = false;
		try {
			signedIn = await hasSession();
		} catch (e: any) {
			// Firebase's chunk failed to load: the signup modal is the safe way on (a signed-in visitor can still log in there).
			console.error("[CountryMap] the session check failed:", e?.message || e);
		}
		if (signedIn) {
			navigateToApp();
			return;
		}
		setSignupOrigin("countrymap", "scratch");
		scratchGate.requestPlan();
	}, [scratchGate]);

	const onLogin = useCallback(() => setSignedIn(true), []);
	/** back to idle, when the visitor moves on to another route after a save or an error */
	const reset = useCallback(() => {
		if (!busy.current) setStatus("idle");
	}, []);

	return { request, status, saved, reset, isPhone, gate: gateName === "mine" ? mineGate : routeGate, onLogin, startFresh, scratchGate };
}
